import type { Arrow, AnimatingArrow, GameState, Level } from './types';
import { calcMove, expandArrow, buildAnimPath } from './physics';

// ms per cell of travel — keeps speed consistent regardless of arrow length
const MS_PER_CELL_EXIT   = 19;
const MS_PER_CELL_BOUNCE = 28;
const MIN_EXIT_MS        = 90;
const MIN_BOUNCE_MS      = 65;

export function createGameState(level: Level): GameState {
  return {
    arrows: level.arrows.map(a => ({ ...a, points: a.points.map(p => ({ ...p })) })),
    lives: 3,
    status: 'playing',
    animating: null,
    erroredArrows: [],
  };
}

export function tryMoveArrow(
  state: GameState,
  arrow: Arrow,
  level: Level,
  now: number
): GameState {
  if (state.animating || state.status !== 'playing') return state;

  const others  = state.arrows.filter(a => a.id !== arrow.id);
  const result  = calcMove(arrow, others, level);
  const arrowLength = expandArrow(arrow).length;

  let anim: AnimatingArrow;

  if (result.canExit) {
    // maxProgress = head steps to exit + arrowLength so the tail also leaves
    const maxProgress = result.steps + arrowLength;
    anim = {
      arrow,
      expandedPath: buildAnimPath(arrow, result.steps + arrowLength + 2),
      arrowLength,
      maxProgress,
      startTime: now,
      duration: Math.max(MIN_EXIT_MS, maxProgress * MS_PER_CELL_EXIT),
      type: 'exit',
    };
  } else {
    // Bounce: go forward result.steps cells then return (min 0.5 for visual feedback)
    const maxProgress = Math.max(0.5, result.steps);
    anim = {
      arrow,
      expandedPath: buildAnimPath(arrow, Math.ceil(maxProgress) + 1),
      arrowLength,
      maxProgress,
      startTime: now,
      duration: Math.max(MIN_BOUNCE_MS, maxProgress * MS_PER_CELL_BOUNCE * 2),
      type: 'bounce',
    };
  }

  return { ...state, animating: anim };
}

export function finishAnimation(state: GameState): GameState {
  if (!state.animating) return state;

  const { type, arrow } = state.animating;
  let arrows        = state.arrows;
  let lives         = state.lives;
  let erroredArrows = state.erroredArrows;

  if (type === 'exit') {
    arrows        = arrows.filter(a => a.id !== arrow.id);
    erroredArrows = erroredArrows.filter(id => id !== arrow.id);
  } else {
    lives         = Math.max(0, lives - 1);
    erroredArrows = erroredArrows.includes(arrow.id) ? erroredArrows : [...erroredArrows, arrow.id];
  }

  const status = arrows.length === 0 ? 'won' : lives === 0 ? 'lost' : 'playing';
  return { arrows, lives, status, animating: null, erroredArrows };
}
